import { Button } from '@/components/Button/Button';
import { DropdownMenuItem } from '@/components/ui/DropdownMenu';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { Form, useForm } from '@/components/ui/Form';
import { Icon } from '@/components/ui/Icon';
import useAnalytics from '@/lib/analytics/useAnalytics';
import { cn } from '@/utils/utils';
import type { Annotation } from '@anaralabs/lector';
import { format, formatDistanceToNowStrict } from 'date-fns';
import {
  Check,
  Copy,
  Ellipsis,
  MessageSquare,
  PenLine,
  Trash,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import { useDocumentAnnotations } from './use-document-annotations';

export const formatTimeAgo = (date: Date) => {
  const utcDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);

  const distance = formatDistanceToNowStrict(utcDate, {
    addSuffix: false,
  });

  if (distance.includes('second')) return 'just now';
  if (distance.includes('minute') || distance.includes('hour'))
    return `${distance} ago`;

  return format(utcDate, 'MMM d, yyyy');
};

export const AnnotationComment = ({
  annotation,
}: { annotation: Annotation }) => {
  const { handleCopyContents, handleUpdateAnnotation, handleCopyToChat } =
    useDocumentAnnotations(annotation.metadata?.chatbotId as string);
  const [isEditing, setIsEditing] = useState(false);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [wasClicked, setWasClicked] = useState(false);
  const { track } = useAnalytics();
  const handleSaveComment = useCallback(
    (data: { comment: string }) => {
      handleUpdateAnnotation(annotation.id, {
        ...annotation,
        comment: data.comment,
      });
      setIsEditing(false);
      track('annotation_comment_updated', {
        location: 'pdf_viewer',
      });
    },
    [annotation.id, handleUpdateAnnotation],
  );

  const form = useForm({
    defaultValues: {
      comment: annotation.comment || '',
    },
    schema: z.object({
      comment: z.string(),
    }),
  });

  const handleTooltipClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setWasClicked(true);
  }, []);

  const userDisplayName = annotation.metadata?.userDisplayName as string;
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = '1.5em';
    const maxHeight = 15;
    const scrollHeight = textarea.scrollHeight;
    const lineHeight = Number.parseFloat(getComputedStyle(textarea).lineHeight);
    const lines = Math.min(Math.ceil(scrollHeight / lineHeight), maxHeight);
    textarea.style.height = `${lines * 1.5}em`;
  }, [isEditing]);

  useEffect(() => {
    if (textareaRef.current) {
      adjustHeight();
    }
  }, [adjustHeight, isEditing, annotation.comment]);

  useEffect(() => {
    if (isEditing) {
      setTimeout(() => {
        form.setFocus('comment');
      }, 200);
    }
  }, [isEditing, form]);

  const { ref: formRef, ...formRegister } = form.register('comment');

  const setRefs = (element: HTMLTextAreaElement | null) => {
    textareaRef.current = element;
    formRef(element);
  };

  useEffect(() => {
    form.reset({ comment: annotation.comment || '' });
  }, [annotation.comment, form]);

  return (
    <div
      className="flex flex-col gap-2 bg-popover border rounded-lg p-3 shadow-md w-[296px]"
      onClick={handleTooltipClick}
      onMouseLeave={(e) => {
        if (wasClicked) {
          e.stopPropagation();
          e.preventDefault();
        }
      }}
    >
      <div className="flex flex-row justify-between">
        <div className="flex flex-row gap-2 items-center">
          <div className="w-5 h-5 rounded-full bg-popover-hover items-center justify-center text-center">
            <p className="text-xs text-muted-foreground uppercase px-1 py-[5px] leading-[11px]">
              {userDisplayName?.length ? userDisplayName.charAt(0) : 'U'}
            </p>
          </div>
          <p className="text-sm font-medium"> {userDisplayName || 'User'}</p>
          <p className="text-xs leading-5 pt-0.5 font-medium text-muted-foreground/60">
            {formatTimeAgo(annotation.updatedAt)}
          </p>
        </div>
        <DropdownMenu
          onOpenChange={() => {
            setIsConfirmingDelete(false);
          }}
        >
          <DropdownMenuTrigger asChild>
            <Button
              className="h-7 w-7 font-medium hover:bg-popover-hover"
              variant="ghost"
              id="noteMenuButton"
              size="xs"
            >
              <Icon as={Ellipsis} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="w-[186px] z-[55]"
            sideOffset={6}
          >
            <DropdownMenuItem onClick={() => setIsEditing(true)}>
              <Icon as={PenLine} />
              Edit comment
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                handleCopyContents({
                  text: annotation.metadata?.text as string,
                  comment: annotation.comment as string,
                  chatbotId: annotation.metadata?.chatbotId as string,
                });
                track('annotation_comment_copied', {
                  location: 'pdf_viewer',
                });
              }}
            >
              <Icon as={Copy} />
              Copy contents
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                handleCopyToChat(
                  annotation.metadata?.text as string,
                  annotation.highlights,
                  annotation.metadata?.chatbotId as string,
                );
                track('annotation_comment_added_to_chat', {
                  location: 'pdf_viewer',
                });
              }}
            >
              <Icon as={MessageSquare} />
              Add to chat
            </DropdownMenuItem>
            <DropdownMenuItem
              className={cn(
                isConfirmingDelete &&
                  'text-destructive-solid bg-destructive hover:!bg-destructive hover:!text-destructive-solid',
              )}
              onClick={(e) => {
                if (!isConfirmingDelete) {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsConfirmingDelete(true);
                } else {
                  handleSaveComment({ comment: '' });
                  track('annotation_comment_deleted', {
                    location: 'pdf_viewer',
                  });
                }
              }}
            >
              <Icon as={isConfirmingDelete ? Check : Trash} />
              {isConfirmingDelete ? 'Confirm' : 'Delete comment'}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSaveComment)}
          className="w-full"
        >
          <div className="w-full flex flex-col items-center gap-3 pl-1">
            <textarea
              {...formRegister}
              ref={setRefs}
              disabled={!isEditing}
              className={`w-full text-sm focus:outline-none placeholder:text-muted-foreground bg-popover resize-none p-0 leading-[1.5] min-h-5 max-h-[160px] overflow-y-auto scrollbar-hide ${
                isEditing &&
                'min-h-5 line-clamp-10 break-words whitespace-pre-wrap'
              }`}
              placeholder="Add a comment..."
              style={{
                height: '20px',
                boxSizing: 'content-box',
                display: isEditing ? 'block' : '-webkit-box',
                WebkitLineClamp: isEditing ? 'none' : '4',
                WebkitBoxOrient: 'vertical',
              }}
              onInput={adjustHeight}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (form.formState.isValid) {
                    form.handleSubmit(handleSaveComment)();
                  }
                }
              }}
            />
            {isEditing && (
              <div className="w-full flex flex-row gap-2 justify-end">
                <Button
                  size="xs"
                  variant="secondary"
                  className="px-2.5"
                  onClick={() => {
                    setIsEditing(false);
                    form.reset({ comment: annotation.comment || '' });
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  size="xs"
                  className="px-2.5"
                  disabled={!form.formState.isValid && form.formState.isDirty}
                >
                  Save
                </Button>
              </div>
            )}
          </div>
        </form>
      </Form>
    </div>
  );
};
